import { MessageCircle, Send, Instagram, Phone, MessageSquare, Mail, Link as LinkIcon } from "lucide-react";
import { useSite } from "@/contexts/SiteContext";
import { activeChannels, filterChannelsByKeys } from "@/lib/contactChannels";
import { trackLead } from "@/lib/analyticsEvents";

// Stil branduit per tip de canal — culori oficiale acolo unde există, altfel
// accentul site-ului (var(--ct-gold)). Iconițele sunt din lucide-react.
// Exportat ca sursă unică de adevăr — WhatsAppFloat.jsx refolosește
// CHANNEL_META/metaFor/isAppOrWebLink în loc să-și reimplementeze logica.
export const CHANNEL_META = {
  whatsapp: { icon: MessageCircle, bg: "#25D366", color: "#0a3d24", label: "WhatsApp" },
  viber: { icon: MessageCircle, bg: "#7360F2", color: "#ffffff", label: "Viber" },
  telegram: { icon: Send, bg: "#229ED9", color: "#ffffff", label: "Telegram" },
  messenger: { icon: MessageCircle, bg: "#0084FF", color: "#ffffff", label: "Messenger" },
  instagram: {
    icon: Instagram,
    bg: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)",
    color: "#ffffff",
    label: "Instagram",
  },
  phone: { icon: Phone, bg: "var(--ct-gold)", color: "var(--ct-btn-primary-text)", label: "Sună-mă" },
  sms: { icon: MessageSquare, bg: "var(--ct-gold)", color: "var(--ct-btn-primary-text)", label: "SMS" },
  email: { icon: Mail, bg: "var(--ct-gold)", color: "var(--ct-btn-primary-text)", label: "Email" },
  custom_url: { icon: LinkIcon, bg: "var(--ct-gold)", color: "var(--ct-btn-primary-text)", label: "Contact" },
};

export const metaFor = (type) => CHANNEL_META[type] || CHANNEL_META.custom_url;

// tel:/sms:/mailto: se deschid în aceeași filă (nu are sens un tab nou pentru
// un handler de sistem); orice alt href (wa.me, viber://, t.me, m.me, ig.me,
// URL personalizat) e tratat ca link extern.
export const isAppOrWebLink = (href) => !/^(tel:|sms:|mailto:)/.test(href || "");

// Randează butoanele canalelor de contact active (site.contact_channels).
// variant="row" — butoane inline, pentru Hero/CTA/bandă.
// variant="stack" — listă verticală într-un mic card, pentru floating/contact.
// onNavigate(ch) — callback opțional apelat la click (ex: WhatsAppFloat închide meniul).
// only — array opțional de chei de canal (`ch.key`, NU `ch.type`) la care se
// restrânge randarea; gol/absent = toate canalele active. Folosit de
// <SectionCta cta_channels=.../> ca să arate doar un subset de canale.
export default function ContactChannels({ variant = "row", className = "", onNavigate, only }) {
  const { site } = useSite();
  const channels = filterChannelsByKeys(activeChannels(site), only);
  if (!channels.length) return null;

  const isStack = variant === "stack";

  const handleClick = (ch) => () => {
    trackLead(`contact_channel_${ch.type}`);
    onNavigate?.(ch);
  };

  return (
    <div
      data-testid="contact-channels"
      data-variant={variant}
      className={[
        "flex gap-3",
        isStack ? "flex-col items-stretch p-2 rounded-2xl border" : "flex-wrap items-center",
        className,
      ].filter(Boolean).join(" ")}
      style={isStack ? { background: "var(--ct-surface)", borderColor: "var(--ct-border)" } : undefined}
    >
      {channels.map((ch) => {
        const m = metaFor(ch.type);
        const Icon = m.icon;
        return (
          <a
            key={ch.key || `${ch.type}-${ch.href}`}
            href={ch.href}
            onClick={handleClick(ch)}
            {...(isAppOrWebLink(ch.href) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            data-testid={`contact-channel-${ch.type}`}
            aria-label={ch.label || m.label}
            className={[
              "inline-flex items-center gap-2 font-medium transition-transform hover:scale-[1.03]",
              isStack ? "w-full px-4 py-3 rounded-xl text-sm" : "px-4 py-2.5 rounded-full text-sm",
            ].join(" ")}
            style={{ background: m.bg, color: m.color, boxShadow: "0 10px 24px -12px rgba(0,0,0,0.35)" }}
          >
            <Icon size={18} />
            <span>{ch.label || m.label}</span>
          </a>
        );
      })}
    </div>
  );
}
